/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Routes, Route, useNavigate, Location } from "react-router-dom";
import { Viewer } from "components/auth/types";
import {
  Feature,
  QueryInterestProfile_interest as InterestType,
  QueryInterestProfile_interest_interestList_property as PropertyType,
  QueryInterestProfile_interest_user as UserType,
} from "types/gql-types";
import { isPropertyFeatureEnabled } from "pages/dashboard/interests";
import { deriveCanAssignOwner } from "pages/dashboard/interests/interests-table/can-assign-owner";
import OwnerControlContainer from "pages/dashboard/interests-profile/components/owner-control-container";
import { toOwnerSummaries } from "components/common/owners-editor/use-owner-changes";
import Documents from "../../interests-documents/components/index";
import InterestUpdatePage from "../../interests-update/interest-update-page";
import Star from "../../interests/interests-table/star";
import Activity from "./activity-page";
import Applications from "./applications-page";
import Actions from "./actions";
import Navigation from "./navigation";
import Metadata from "./metadata";
import styles from "./index.module.css";

export const links = [
  { name: "Activity", to: "activity" },
  { name: "Applicant Info", to: "update" },
  {
    name: "Documents",
    to: "documents",
    feature: Feature.DOCUMENT_UPLOAD,
  },
];

import type { Action } from "pages/dashboard/interests-profile/components/reason-modal";

type Props = {
  viewer: Viewer;
  location: Location;
  onStar: (bool: boolean) => void;
  setBatchActionsModalFocusedAction: React.Dispatch<React.SetStateAction<Action | undefined>>;
  currentInterestID: string;
  householdMember0FirstName: string | null;
  householdMember0LastName: string | null;
  interestListID: string;
  property: PropertyType;
  householdCount: string;
  householdGrossAnnualIncome: number | null;
  status: string;
  onSubmit: () => Promise<void>;
  sendFullAppToInterestsEnabled: boolean;
  interest: InterestType;
  user: UserType | null;
}

const formatName = ({ householdMember0FirstName, householdMember0LastName }: Props) =>
  [householdMember0FirstName, householdMember0LastName].filter(Boolean).join(" ");

export const ApplicantProfileView: React.FC<Props> = (props) => {
  const { interest, currentInterestID, interestListID, onSubmit, user,
    property: { features } } = props;
  if (typeof currentInterestID === "undefined") return null;

  const navigate = useNavigate();
  const updateProps = {
    interestID: interest.id,
    interestListID,
    onSubmit,
    onCancel: () => {
      const { pathname } = props.location;
      const idx = pathname.indexOf("/interests/");
      let to = "..";
      if (idx > -1) to = pathname.slice(0, idx).replace(/\/$/, "");
      navigate(to);
    },
  };
  const documentFeatureEnabled = isPropertyFeatureEnabled(features, Feature.DOCUMENT_UPLOAD);

  const currentInterestName = formatName(props);

  // When false the owners still display read-only but add/remove are hidden.
  const canAssignOwner = deriveCanAssignOwner(props.viewer);

  return (
    <div className={styles.profile}>
      <Box sx={{ backgroundColor: "#f5fbfc", ml: -2, pl: 4, pt: 2 }}>

        <Box className={styles.labels} sx={{ "& > a": { marginTop: "0 !important" } }}>
          <Star
            currentInterestID={interest.id}
            hasStar={!!interest.hasEligibility}
            onStar={props.onStar}
          />
          <Box component="span" className={styles.label} sx={{ lineHeight: "32px" }}>
            {currentInterestName}
          </Box>
          <Box sx={{ ml: 2, display: "inline-flex", alignItems: "center" }}>
            <OwnerControlContainer
              interestId={interest.id}
              owners={toOwnerSummaries(interest.owners)}
              currentUserId={props.viewer.id}
              currentUserName={props.viewer.fullName ?? ""}
              canAssign={canAssignOwner}
              applicantName={currentInterestName}
            />
          </Box>
        </Box>

        <Metadata
          householdCount={props.householdCount}
          householdGrossAnnualIncome={ props.householdGrossAnnualIncome }
          position={interest.positionNumber}
          loginDetails={user?.loginEmail ?? user?.loginPhone}
          interestListName={interest.interestList.name}
        />

        <Stack
          spacing={{ sm: 1, md: 2 }}
          direction={{ sm: "column", lg: "row" }}
          justifyContent={{ sm: "flex-start", lg: "space-between" }}
          sx={{ ml: -2, borderBottom: 1, borderColor: "divider" }}
        >
          <Navigation interestId={interest.id} />

          <Actions
            currentInterestName={currentInterestName}
            property={props.property}
            currentInterestID={interest.id}
            interestListID={props.interestListID}
            status={props.status}
            setBatchActionsModalFocusedAction={props.setBatchActionsModalFocusedAction}
            sendFullAppToInterestsEnabled={ props.sendFullAppToInterestsEnabled }
          />
        </Stack>
      </Box>

      <div className={styles.container}>
        <div className={styles.content}>
          <Routes>
            <Route index element={<Activity interestID={currentInterestID} />} />
            <Route path="activity" element={<Activity interestID={currentInterestID} />} />
            <Route path="update" element={<InterestUpdatePage {...updateProps} />} />
            <Route path="documents" element={<Documents path="" interestID={currentInterestID} enabled={documentFeatureEnabled} />} />
            <Route path="applications" element={<Applications interestId={currentInterestID} viewer={props.viewer} />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};
